import { Box, Chip, ChipOwnProps, LinearProgress, SxProps, Theme, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Minimum_Order_Value_Type_Enum } from '@kheops/graphql';
import PackageImage from '../../assets/images/3d-icons/package.png';
import { formatMinimumOrderValue } from '../utils/common.utils';

interface MinimumOrderValueChipProps {
  minimumOrderValue?: number;
  minimumOrderValueType?: Minimum_Order_Value_Type_Enum;
  minimumOrderValueProgression?: number;
  negotiable?: boolean;
  disableTooltip?: boolean;
  enableProgress?: boolean;
  sx?: SxProps;
  color?: ChipOwnProps['color'];
  variant?: ChipOwnProps['variant'];
}

interface ChipStatus {
  color?: 'success' | 'error';
  icon: {
    background: (theme: Theme) => string;
    cube?: (theme: Theme) => string;
  }
}

export default function MinimumOrderValueChip({
  minimumOrderValue,
  minimumOrderValueType = Minimum_Order_Value_Type_Enum.Price,
  minimumOrderValueProgression,
  negotiable,
  disableTooltip,
  enableProgress,
  sx,
  color,
  variant = 'tag',
}: MinimumOrderValueChipProps): React.JSX.Element {
  const { t } = useTranslation(['basket', 'contracts', 'common', 'packaging']);

  const formattedMoV = useMemo((): string => {
    return typeof minimumOrderValue === 'number'
      ? formatMinimumOrderValue(t, minimumOrderValue, minimumOrderValueType, negotiable)
      : t('common:to_confirm');
  }, [minimumOrderValue, minimumOrderValueType, negotiable]);

  const formattedCurrentMinimumOrderValue = useMemo((): string => {
    return formatMinimumOrderValue(t, minimumOrderValueProgression!, minimumOrderValueType);
  }, [minimumOrderValue, minimumOrderValueProgression, minimumOrderValueType]);

  const progress = useMemo(() => {
    if (!minimumOrderValueProgression || typeof minimumOrderValue !== 'number') {
      return 0;
    }

    // Divide by 0 returns Infinity in JS which works here, that's why no fallback has been added
    return (minimumOrderValueProgression / minimumOrderValue) * 100;
  }, [minimumOrderValueProgression, minimumOrderValue]);

  const chipStatus = useMemo((): ChipStatus => {
    if (!enableProgress) {
      return {
        icon: {
          background: (theme: Theme) => theme.palette.marketing3.main,
        },
      };
    }

    return progress >= 100
      ? {
        color: 'success',
        icon: {
          background: (theme: Theme) => theme.palette.success.main,
          cube: (theme: Theme) => theme.palette.onSuccess.main,
        },
      }
      : {
        color: 'error',
        icon: {
          background: (theme: Theme) => theme.palette.error.main,
          cube: (theme: Theme) => theme.palette.onError.main,
        },
      };
  }, [enableProgress, progress]);

  const title = useMemo(() => {
    if (enableProgress) {
      return t('basket:minimum_order_value_progress', { currentValue: formattedCurrentMinimumOrderValue, minimumOrderValue: formattedMoV });
    }

    return t('common:minimum_order_value');
  }, [enableProgress, minimumOrderValueProgression]);

  return (
    <Tooltip title={title} disableHoverListener={disableTooltip}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '100px',
          ...sx,
        }}
      >
        <Chip
          variant={variant}
          icon={<img src={PackageImage} />}
          label={formattedMoV}
          color={chipStatus.color || color}
          sx={{
            pr: 1.5,
          }}
        />
        {!!enableProgress && progress < 100 && (
          <LinearProgress
            sx={{
              bgcolor: 'marketing3.main',
              height: '2px',
              position: 'absolute',
              width: '100%',
              bottom: '0px',
            }}
            color="accent"
            variant="determinate"
            value={progress}
          />
        )}
      </Box>
    </Tooltip>
  );
}
